(() => {
  // Per-side logic: text character count, font preview, artwork filename
  // display. Scoped to a single personalization panel (front or back) so it
  // works correctly whether one or two panels are present on the page.
  const initSide = (root) => {
    if (!root || root.dataset.ufSideReady === 'true') return;
    root.dataset.ufSideReady = 'true';

    const textInput = root.querySelector('[data-uf-engraving-text]');
    const count = root.querySelector('[data-uf-character-count]');
    const preview = root.querySelector('[data-uf-preview-text]');
    const fontSelect = root.querySelector('[data-uf-font-select]');
    const previewFallback = preview?.textContent || 'Your personalization';

    const updatePreview = () => {
      if (!textInput) return;
      if (preview) preview.textContent = textInput.value.trim() || previewFallback;
      if (count) count.textContent = String(textInput.value.length);
    };

    const customFontField = root.querySelector('[data-uf-custom-font-field]');
    const customFontInput = root.querySelector('[data-uf-custom-font]');

    const genericFallback = (fontName) => {
      const scriptFonts = ['Brush Script MT', 'Segoe Script', 'Snell Roundhand', 'Apple Chancery', 'Bradley Hand', 'Lucida Handwriting'];
      const serifFonts = ['Georgia', 'Times New Roman', 'Garamond', 'Baskerville', 'Palatino', 'Bookman', 'Copperplate'];
      const monoFonts = ['Courier New', 'American Typewriter'];
      if (scriptFonts.includes(fontName)) return 'cursive';
      if (serifFonts.includes(fontName)) return 'serif';
      if (monoFonts.includes(fontName)) return 'monospace';
      return 'sans-serif';
    };

    const updateFont = () => {
      if (!fontSelect || !preview) return;
      const isCustom = fontSelect.value === 'Other font requested';
      if (customFontField) customFontField.hidden = !isCustom;
      if (customFontInput) {
        customFontInput.disabled = !isCustom;
        customFontInput.required = isCustom;
      }

      const requestedFont = isCustom ? customFontInput?.value.trim() : fontSelect.value;
      if (!requestedFont) {
        preview.style.fontFamily = 'Georgia, serif';
        return;
      }
      const escapedFont = requestedFont.replace(/["\\]/g, '');
      preview.style.fontFamily = `"${escapedFont}", ${genericFallback(escapedFont)}`;
    };

    textInput?.addEventListener('input', updatePreview);
    fontSelect?.addEventListener('change', updateFont);
    customFontInput?.addEventListener('input', updateFont);
    updatePreview();
    updateFont();

    const artworkInput = root.querySelector('[data-uf-artwork-input]');
    const artworkFile = root.querySelector('[data-uf-upload-file]');
    artworkInput?.addEventListener('change', () => {
      if (!artworkFile) return;
      artworkFile.textContent = artworkInput.files?.[0]?.name || 'No file selected';
    });
  };

  // Section-level logic: image gallery, variant selection, order summary,
  // and the optional "add engraving to the back" toggle. These are shared
  // across the whole product, not duplicated per side.
  const initProduct = (root) => {
    if (!root || root.dataset.ufReady === 'true') return;
    root.dataset.ufReady = 'true';

    const mainImage = root.querySelector('.uf-product__main-image');
    const galleryButtons = root.querySelectorAll('[data-uf-gallery-thumb]');

    galleryButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!mainImage || !button.dataset.imageSrc) return;

        // image_tag adds a responsive srcset. Updating only `src` leaves the
        // browser free to keep displaying the previous image from that srcset.
        // Replace both attributes so the selected thumbnail always becomes the
        // visible main image.
        mainImage.src = button.dataset.imageSrc;
        if (button.dataset.imageSrcset) {
          mainImage.srcset = button.dataset.imageSrcset;
          mainImage.sizes = '(min-width: 990px) 46vw, 100vw';
        } else {
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
        }
        mainImage.alt = button.dataset.imageAlt || '';

        galleryButtons.forEach((item) => {
          item.classList.remove('is-active');
          item.setAttribute('aria-pressed', 'false');
        });
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      });
    });

    const variantSelect = root.querySelector('[data-uf-variant-select]');
    const variantInput = root.querySelector('[data-uf-variant-id]');
    const addButton = root.querySelector('[data-uf-add-button]');
    const addLabel = root.querySelector('[data-uf-add-label]');

    const summaryPrice = root.querySelector('[data-uf-summary-price]');
    const summaryTotal = root.querySelector('[data-uf-summary-total]');
    const quantityInput = root.querySelector('input[name="quantity"]');

    // Back-engraving pricing: reads a merchant-configured product option
    // (e.g. "Engraving": "Front only" / "Front + Back") so checking the
    // back-engraving box switches to the correctly priced real Shopify
    // variant, rather than just a cosmetic UI state.
    const backToggle = root.querySelector('[data-uf-back-toggle]');
    const backPanel = root.querySelector('[data-uf-back-panel]');
    const priceDiffEl = root.querySelector('[data-uf-back-price-diff]');
    const variantDataEl = root.querySelector('[data-uf-variant-data]');

    let variantData = null;
    let engravingOptionIndex = -1; // 0-based index into option1/2/3
    if (variantDataEl) {
      try {
        variantData = JSON.parse(variantDataEl.textContent);
        if (variantData.engravingOptionName) {
          engravingOptionIndex = (variantData.optionNames || []).findIndex(
            (name) => String(name).trim().toLowerCase() === String(variantData.engravingOptionName).trim().toLowerCase()
          );
        }
      } catch (_error) {
        variantData = null;
      }
    }

    const optionKey = (index) => `option${index + 1}`;

    const currentVariantId = () => {
      if (variantInput?.value) return variantInput.value;
      const option = variantSelect?.options[variantSelect.selectedIndex];
      return option?.value || '';
    };

    const findVariantById = (id) => (variantData?.variants || []).find((v) => String(v.id) === String(id));

    // Given a target value for the engraving option, find the variant that
    // matches the current variant on every OTHER option (color, size, etc.)
    // but has the engraving option set to the target value.
    const findVariantWithEngravingValue = (targetValue) => {
      if (!variantData || engravingOptionIndex === -1) return null;
      const current = findVariantById(currentVariantId());
      if (!current) return null;
      return (variantData.variants || []).find((v) => {
        if (String(v[optionKey(engravingOptionIndex)]) !== String(targetValue)) return false;
        return (variantData.optionNames || []).every((_name, i) => {
          if (i === engravingOptionIndex) return true;
          return v[optionKey(i)] === current[optionKey(i)];
        });
      }) || null;
    };

    const applyVariant = (variant) => {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      if (variantSelect) {
        const match = Array.from(variantSelect.options).find((o) => String(o.value) === String(variant.id));
        if (match) variantSelect.value = match.value;
      }
      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'Add personalized item to cart' : 'Sold out';
      if (summaryPrice) summaryPrice.textContent = variant.priceFormatted;
      if (summaryTotal) summaryTotal.textContent = variant.priceFormatted;
    };

    const updateSummary = () => {
      const option = variantSelect?.options[variantSelect.selectedIndex];
      const priceText = option?.dataset.price || summaryPrice?.textContent || '';
      if (summaryPrice) summaryPrice.textContent = priceText;
      if (summaryTotal) summaryTotal.textContent = priceText;
    };

    const showPriceDiff = () => {
      if (!priceDiffEl || !variantData || engravingOptionIndex === -1) return;
      const frontVariant = findVariantWithEngravingValue(variantData.frontOnlyValue);
      const backVariant = findVariantWithEngravingValue(variantData.backIncludedValue);
      if (!frontVariant || !backVariant) { priceDiffEl.hidden = true; return; }
      const diffCents = Number(backVariant.price) - Number(frontVariant.price);
      if (diffCents <= 0) { priceDiffEl.hidden = true; return; }
      const formatted = (diffCents / 100).toLocaleString(undefined, { style: 'currency', currency: variantData.shopCurrency || 'USD' });
      priceDiffEl.textContent = `+${formatted}`;
      priceDiffEl.hidden = false;
    };

    // Re-resolves the correct variant for whatever is currently selected
    // (any other options) combined with the current back-toggle state.
    // Called both when another option changes and when the toggle changes,
    // so the two selections always agree on a single real variant.
    const syncEngravingVariant = () => {
      if (!variantData || engravingOptionIndex === -1 || !backToggle) return;
      const targetValue = backToggle.checked ? variantData.backIncludedValue : variantData.frontOnlyValue;
      const match = findVariantWithEngravingValue(targetValue);
      if (match) applyVariant(match);
    };

    variantSelect?.addEventListener('change', () => {
      const option = variantSelect.options[variantSelect.selectedIndex];
      if (variantInput) variantInput.value = option.value;
      const available = option.dataset.available === 'true';
      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = available ? 'Add personalized item to cart' : 'Sold out';
      updateSummary();
      // If the customer just changed a different option (color, size), make
      // sure the engraving side of the variant still matches the toggle.
      syncEngravingVariant();
      showPriceDiff();
    });
    quantityInput?.addEventListener('change', updateSummary);
    updateSummary();
    showPriceDiff();

    // Mounts/reveals the back personalization panel. The panel and its canvas
    // are already fully initialized even while hidden (initAll below runs on
    // every [data-uf-personalization-side] regardless of visibility), so this
    // only needs to show/hide it and resolve the correctly priced variant.
    //
    // Important: the `hidden` attribute only affects visual display — it does
    // NOT stop form fields from being submitted. Without explicitly disabling
    // the back panel's fields while hidden, their default values (font,
    // sizing, etc.) get sent as cart properties even when the customer never
    // touched the back side at all. Disabled fields are correctly excluded
    // from form submission, so toggle disabled state in lockstep with hidden.
    const setBackFieldsDisabled = (disabled) => {
      if (!backPanel) return;
      backPanel.querySelectorAll('input, select, textarea, button').forEach((field) => {
        field.disabled = disabled;
      });
    };
    setBackFieldsDisabled(backPanel ? backPanel.hidden : true);

    backToggle?.addEventListener('change', () => {
      if (backPanel) backPanel.hidden = !backToggle.checked;
      setBackFieldsDisabled(!backToggle.checked);
      syncEngravingVariant();
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-uf-engraving-product]').forEach(initProduct);
    scope.querySelectorAll('[data-uf-personalization-side]').forEach(initSide);
  };

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
